import { memo } from "react";
import {
  HELM_CALCULATED_LABEL,
  INCOMPLETE_HISTORY,
  brokerFigureTitle,
  rithmicAsOf,
  riskCoverage,
  useSettings,
  type BrokerFigure,
} from "@helm/core";
import { formatCents } from "@helm/ui";
import { usePopover } from "../components/Popover";
import { useTradingView, useTradingViewSlice, type TradingView } from "../viewmodel/account";
import { sameList, shallowEqual } from "../viewmodel/slice";
import { BrokerRiskFigures } from "./BrokerRiskFigures";
import { helmFigurePending, loadFirmRuntime, useFirmRuntime } from "./firmGate";
import "./riskCoverage.css";

/** Broker source disclosure; operational risk controls remain separate. */
export const RiskCoverage = memo(function RiskCoverage({
  connected,
}: {
  connected: boolean;
}): React.JSX.Element {
  const rows = useTradingViewSlice(coverageRows, (a, b) => sameList(a, b, shallowEqual));
  const privacy = useSettings((s) => s.settings.privacy);
  const pop = usePopover();
  const unavailable = !connected || rows.length === 0 || rows.some((a) => a.financeReason !== "");
  const label = unavailable ? "Unavailable" : rows.length > 1 ? "Per account" : "Rithmic finance";
  // The risk strip (ADR-0045/0047): one account's room figures with their source tag (Rithmic, or
  // HELM-calculated from the owner's firm rules); with several accounts in view they stay per
  // account (in the panel and the Accounts table).
  const one = rows.length === 1 ? rows[0] : undefined;
  return (
    <div className="risk-coverage">
      <button
        type="button"
        className={`risk-coverage-trigger${unavailable ? " caution" : ""}`}
        {...pop.triggerProps}
        aria-label={`Account sources: ${label}`}
      >
        <span>Rithmic account</span>
        <strong>{label}</strong>
      </button>
      {one && (
        <div className="risk-strip" aria-label="Risk figures">
          <StripFigure
            name="DD room"
            value={one.ddValue}
            pending={one.ddPending}
            tag={one.ddTag}
            tone={one.ddTone}
            title={one.ddTitle}
            privacy={privacy}
            figure="trailing_drawdown"
            source={one.ddSource}
          />
          <StripFigure
            name="Daily loss"
            value={one.dlValue}
            pending={one.dlPending}
            tag={one.dlTag}
            tone={one.dlTone}
            title={one.dlTitle}
            privacy={privacy}
            figure="daily_loss"
            source={one.dlSource}
          />
        </div>
      )}
      {pop.open && (
        <div
          className="popover risk-coverage-panel"
          {...pop.panelProps}
          role="dialog"
          aria-label="Account sources and coverage"
          tabIndex={-1}
        >
          <h2>Account sources and coverage</h2>
          <BrokerAccountDetails />
          <button
            type="button"
            className="btn"
            onClick={() => {
              pop.setOpen(false);
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
});
/**
 * "DD room" in the desk's position strip (owner 2026-10-04 UI clean-up; the one place it shows):
 * the selected account's drawdown room with its source in the tooltip, "—" when unknown or while
 * All accounts is selected (room is per account). Clicking it opens the account sources and
 * coverage panel that the old top bar's "Rithmic account" button opened.
 */
export const DdRoomFigure = memo(function DdRoomFigure({
  connected,
}: {
  connected: boolean;
}): React.JSX.Element {
  const rows = useTradingViewSlice(coverageRows, (a, b) => sameList(a, b, shallowEqual));
  const privacy = useSettings((s) => s.settings.privacy);
  const pop = usePopover();
  const one = rows.length === 1 ? rows[0] : undefined;
  const unavailable = !connected || rows.length === 0 || rows.some((a) => a.financeReason !== "");
  const value =
    one === undefined
      ? "—"
      : one.ddValue === null
        ? one.ddPending
          ? "…"
          : "—"
        : formatCents(one.ddValue, { privacy });
  const title =
    one === undefined
      ? rows.length > 1
        ? "Drawdown room is per account: pick one account, or see the Accounts module. Click for account sources."
        : "No Rithmic account. Click for account sources."
      : `${one.ddTag}. ${one.ddTitle}${unavailable ? " Account finance is unavailable." : ""} Click for account sources and coverage.`;
  return (
    <div className="popover-anchor strip-dd">
      <button
        type="button"
        className="strip-kv strip-kv-btn"
        title={title}
        aria-haspopup="dialog"
        data-figure="trailing_drawdown"
        data-source={one?.ddSource}
        {...pop.triggerProps}
      >
        <span className="strip-label">DD room</span>
        <span className="strip-value num">{value}</span>
      </button>
      {pop.open && (
        <div
          className="popover risk-coverage-panel"
          {...pop.panelProps}
          role="dialog"
          aria-label="Account sources and coverage"
          tabIndex={-1}
        >
          <h2>Account sources and coverage</h2>
          <BrokerAccountDetails />
          <button
            type="button"
            className="btn"
            onClick={() => {
              pop.setOpen(false);
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
});

/**
 * The strip's short tag: the figure's own label, "HELM-calculated" for HELM's (the full label is
 * in the tooltip), "Incomplete history" when only that explains a missing figure, and a "≠ Rithmic"
 * mark when the HELM-calculated figure differs from the Rithmic one shown.
 */
export function stripTag(f: BrokerFigure): { tag: string; tone: string } {
  if (f.source === "helm_calculated") return { tag: "TradePrizm-calculated", tone: "source-helm" };
  if (f.source === "unavailable") {
    const incomplete = f.alternate?.label === INCOMPLETE_HISTORY;
    return { tag: incomplete ? INCOMPLETE_HISTORY : f.label, tone: "source-unavailable" };
  }
  const differs = f.note?.startsWith("Differs from Rithmic") === true;
  return {
    tag: differs ? `${f.label} · ≠ TradePrizm` : f.label,
    tone: f.verification === "verified" ? "source-verified" : "",
  };
}

/** " Account balance: Rithmic, as of 10:42:15 CT." for a Rithmic figure (ADR-0056), else "". */
const inputAsOf = (f: BrokerFigure, name: string, ns: bigint | null): string => {
  const asOf = f.source === "rithmic" ? rithmicAsOf(ns) : "";
  return asOf === "" ? "" : ` ${name}: ${asOf}.`;
};

function coverageRows(view: TradingView) {
  return view.accounts
    .filter((a) => view.selection === "all" || a.summary.id === view.activeId)
    .map((a) => {
      const dd = stripTag(a.broker.trailingDrawdown);
      const dl = stripTag(a.broker.dailyLoss);
      return {
        id: a.summary.id,
        financeReason: a.financeReason,
        ddValue: a.broker.trailingDrawdown.valueCents,
        ddPending: helmFigurePending(a.broker.trailingDrawdown),
        ddTag: dd.tag,
        ddTone: dd.tone,
        ddSource: a.broker.trailingDrawdown.source,
        ddTitle:
          titleOf(a.broker.trailingDrawdown) +
          inputAsOf(a.broker.trailingDrawdown, "Account balance", a.asOf.balance),
        dlValue: a.broker.dailyLoss.valueCents,
        dlPending: helmFigurePending(a.broker.dailyLoss),
        dlTag: dl.tag,
        dlTone: dl.tone,
        dlSource: a.broker.dailyLoss.source,
        dlTitle: titleOf(a.broker.dailyLoss) + inputAsOf(a.broker.dailyLoss, "Day P&L", a.asOf.dayPnl),
      };
    });
}

const titleOf = (f: BrokerFigure): string =>
  f.source === "helm_calculated" ? `${HELM_CALCULATED_LABEL}. ${brokerFigureTitle(f)}` : brokerFigureTitle(f);

function StripFigure({
  name,
  value,
  pending,
  tag,
  tone,
  title,
  privacy,
  figure,
  source,
}: {
  name: string;
  value: bigint | null;
  /** No Rithmic value and HELM's own figure still loading: "…", never "—" or a number. */
  pending: boolean;
  tag: string;
  tone: string;
  title: string;
  privacy: boolean;
  figure: string;
  source: string;
}): React.JSX.Element {
  return (
    <span
      className="risk-strip-figure"
      title={title}
      data-figure={figure}
      data-source={source}
      data-pending={pending ? "" : undefined}
    >
      <span>{name}</span>
      <strong className="num">
        {value === null ? (pending ? "…" : "—") : formatCents(value, { privacy })}
      </strong>
      <em className={`source-tag${tone ? ` ${tone}` : ""}`}>{tag}</em>
    </span>
  );
}
/**
 * A Rithmic money value with when Rithmic set it (ADR-0056: Rithmic sends only changed fields, so
 * a value can be from an earlier update of the session): "$50,000.00", then "Rithmic, as of
 * 10:42:15 CT" under it.
 */
function MoneyDd({ text, asOf }: { text: string; asOf: bigint | null }): React.JSX.Element {
  const source = rithmicAsOf(asOf);
  return (
    <dd title={source || undefined}>
      {text}
      {source !== "" && <span className="risk-as-of">{source}</span>}
    </dd>
  );
}

/** Read-only account information shared by desktop/mobile settings and the disclosure. */
export function BrokerAccountDetails({ accountId }: { accountId?: string }): React.JSX.Element {
  const view = useTradingView();
  const privacy = useSettings((s) => s.settings.privacy);
  const rows = view.accounts.filter((a) =>
    accountId ? a.summary.id === accountId : view.selection === "all" || a.summary.id === view.activeId,
  );
  const money = (value: bigint | null) => formatCents(value, { privacy });
  const firm = useFirmRuntime();
  return (
    <>
      <p>
        Account finance is reported by Rithmic. No preset, override, TradePrizm fill or window mark supplies a
        missing account value.
      </p>
      {firm.status === "failed" && (
        <p className="risk-coverage-warning" role="alert" data-firm-runtime="failed">
          TradePrizm-calculated figures from your firm rules are unavailable: they could not be loaded.
          Rithmic figures are shown; trading is not affected.{" "}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              loadFirmRuntime(true);
            }}
          >
            Retry
          </button>
          {firm.retried && (
            <>
              {" "}
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload app
              </button>
            </>
          )}
        </p>
      )}
      {rows.length === 0 && <p>No Rithmic account available.</p>}
      {rows.length > 1 && (
        <p>Coverage is separate for each account. A missing member makes the combined figure unavailable.</p>
      )}
      {rows.map((a) => {
        const coverage = riskCoverage(a);
        return (
          <section key={a.summary.id} data-risk-account={privacy ? undefined : a.summary.id}>
            <h3>
              {privacy
                ? "***"
                : a.summary.brokerName
                  ? `${a.summary.label} · ${a.summary.brokerName}`
                  : a.summary.label}
            </h3>
            <p className={coverage.caution ? "risk-coverage-warning" : ""}>
              <strong>{coverage.label}.</strong>{" "}
              {privacy && coverage.caution ? "Current account finance unavailable." : coverage.detail}
            </p>
            <dl>
              <dt>Rithmic currency</dt>
              <dd>{a.brokerCurrency ?? "Unavailable"}</dd>
              <dt>Rithmic account balance</dt>
              <MoneyDd text={money(a.balanceCents)} asOf={a.asOf.balance} />
              <dt>Day P&amp;L</dt>
              <MoneyDd text={money(a.dayPnlCents)} asOf={a.asOf.dayPnl} />
              <dt>Day closed P&amp;L</dt>
              <MoneyDd text={money(a.realizedCents)} asOf={a.asOf.realized} />
              <dt>Open-position P&amp;L</dt>
              <MoneyDd text={money(a.unrealizedCents)} asOf={a.asOf.unrealized} />
              <dt>Cash on hand</dt>
              <MoneyDd text={money(a.cashOnHandCents)} asOf={a.asOf.cashOnHand} />
              <dt>Closed-position P&amp;L</dt>
              <MoneyDd text={money(a.closedPositionPnlCents)} asOf={a.asOf.closedPositionPnl} />
              <dt>Equity / Net liquidation</dt>
              <dd>Unavailable</dd>
              <dt>Broker account status</dt>
              <dd>Unavailable</dd>
              <dt>Starting allocation</dt>
              <dd>Unavailable</dd>
              <dt>Broker max order / position</dt>
              <dd>Unavailable</dd>
            </dl>
            <BrokerRiskFigures row={a} />
            <p>
              Drawdown and daily-loss room come from Rithmic values first (formula and inputs above) and stay
              “unverified” until matched against R|Trader Pro. Where Rithmic has no figure, TradePrizm
              calculates one from your firm rules and its own daily history, labelled so, and never mixes the
              two. Equity and net liquidation need verified Rithmic fields. Account balance is not assumed to
              be cash or equity. TradePrizm’s own automatic limits are separate: TradePrizm limit ·
              TradePrizm-calculated.
            </p>
          </section>
        );
      })}
    </>
  );
}
